import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { useTemplateColors } from 'PDF/TemplateEngine/TemplateColorsContext';
import { equipmentKeysOf } from 'PDF/TemplateEngine/utils/pivotInspection';
import SectionHeader from 'PDF/TemplateEngine/atoms/SectionHeader';
import StatusTag from 'PDF/TemplateEngine/atoms/StatusTag';

// Body type 'inspection-matrix': por cada sector, una tabla con la actividad
// y una columna por equipo. Cada celda = StatusTag + observación + sugerencia.
function InspectionMatrixSection({ section, data }) {
  const { colors, FONT } = useTemplateColors();
  const cfg = section?.config || {};
  const sectors = Array.isArray(data?.[cfg.sourceKey]) ? data[cfg.sourceKey] : [];
  const eqKeys = equipmentKeysOf(sectors);
  const labels = cfg.equipmentLabels || {};

  const styles = StyleSheet.create({
    sector: { marginTop: '0.25cm' },
    table: { border: `1px solid ${colors.border}`, borderRadius: 4, overflow: 'hidden' },
    thead: {
      flexDirection: 'row',
      backgroundColor: colors.lightBg,
      borderBottom: `1px solid ${colors.border}`,
    },
    th: {
      padding: '0.15cm 0.2cm',
      fontSize: 7.5,
      fontFamily: FONT.bold,
      color: colors.textFade || colors.muted,
      textTransform: 'uppercase',
    },
    tr: { flexDirection: 'row', borderBottom: `1px solid ${colors.border}` },
    actCell: { width: '46%', padding: '0.18cm 0.2cm' },
    actText: { fontSize: 8.5, fontFamily: FONT.base, color: colors.text || colors.darkText },
    eqCell: { flex: 1, padding: '0.18cm 0.2cm', gap: '0.08cm' },
    obs: { fontSize: 8, fontFamily: FONT.base, color: colors.muted, lineHeight: 1.4 },
  });

  const eqWidth = `${Math.floor(54 / Math.max(eqKeys.length, 1))}%`;

  return (
    <View>
      {sectors.map((sec, si) => (
        <View key={si} style={styles.sector}>
          <SectionHeader
            title={sec.sector}
            count={`${(sec.items || []).length} ${
              (sec.items || []).length === 1 ? 'actividad' : 'actividades'
            }`}
          />
          <View style={styles.table}>
            <View style={styles.thead}>
              <Text style={[styles.th, { width: '46%' }]}>Actividad inspeccionada</Text>
              {eqKeys.map((k) => (
                <Text key={k} style={[styles.th, { width: eqWidth }]}>
                  {labels[k] || k}
                </Text>
              ))}
            </View>
            {(sec.items || []).map((item, ii) => (
              <View key={ii} style={styles.tr} wrap={false}>
                <View style={styles.actCell}>
                  <Text style={styles.actText}>{item.act}</Text>
                </View>
                {eqKeys.map((k) => {
                  const cell = item.cells?.[k] || {};
                  const falla = cell.estado === 'FALLA';
                  return (
                    <View key={k} style={[styles.eqCell, { width: eqWidth }]}>
                      <StatusTag tone={falla ? 'err' : 'ok'}>{cell.estado || '—'}</StatusTag>
                      {cell.detalle && cell.detalle !== 'Estado normal' ? (
                        <Text style={styles.obs}>{cell.detalle}</Text>
                      ) : null}
                    </View>
                  );
                })}
              </View>
            ))}
          </View>
        </View>
      ))}
    </View>
  );
}

export default InspectionMatrixSection;
